// SENTINEL: 1_Telemetery - Google Drive Service (Sync, Auth, Cloud)
// Vitality: Healthy (Auth Logic Stabilized)
import { User, Idea, Section, Message, MessageRole } from "../types";
import { config } from "../config";
import { logger } from "./loggingService";
import { isTauri, openExternalUrl, tauriFetch, tauriFetchBinary, buildMultipartBody, fileToUint8Array, blobToUint8Array } from "./tauriFetch";
import { desktopAuthService } from "./desktopAuthService";

declare var gapi: any;
declare var google: any;

const DISCOVERY_DOCS = [
  "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
  "https://docs.googleapis.com/$discovery/rest?version=v1"
];
const SCOPES = config.google.scopes;
const DB_FILENAME = "concept_canvas_db.json";
const ROOT_FOLDER_NAME = "ConceptCanvas AI";
const SESSION_STORAGE_KEY = 'cc_google_session';
const DESKTOP_SESSION_KEY = 'cc_desktop_google_session';

let tokenClient: any;
let gapiInited = false;
let gisInited = false;
let rootFolderId: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let currentUser: User | null = null;
let desktopAccessToken: string | null = null;
let desktopRefreshToken: string | null = null;

const getDesktopToken = (): string | null => {
  if (desktopAccessToken) return desktopAccessToken;
  try {
    const session = localStorage.getItem(DESKTOP_SESSION_KEY);
    if (session) {
      const parsed = JSON.parse(session);
      return parsed.accessToken;
    }
  } catch { }
  return null;
};

class DesktopDriveError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'DesktopDriveError';
  }
}

const desktopDriveRequest = async (url: string, options: { method?: string; headers?: Record<string, string>; body?: string } = {}): Promise<Response> => {
  const token = getDesktopToken();
  if (!token) {
    throw new DesktopDriveError('No access token available', 401);
  }

  const res = await tauriFetch(url, {
    method: options.method || 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    body: options.body
  });

  if (!res.ok && (res.status === 401 || res.status === 403)) {
    throw new DesktopDriveError(`Auth error: ${res.status}`, res.status);
  }

  return res;
};

// Token refresh configuration
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const TOKEN_LIFETIME_MS = 60 * 60 * 1000; // Google tokens expire in 60 minutes

// Session persistence helpers
const saveSession = (user: User, token: any, expiresAt?: number) => {
  try {
    const expiry = expiresAt || (Date.now() + TOKEN_LIFETIME_MS);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
      user,
      token,
      savedAt: Date.now(),
      expiresAt: expiry
    }));
    currentUser = user;
    scheduleTokenRefresh(expiry);
  } catch (e) {
    logger.warn('Auth', 'Failed to save session to localStorage', e);
  }
};

const loadSession = (): { user: User; token: any; expiresAt?: number } | null => {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    const session = JSON.parse(stored);
    // Check if token is expired
    const expiresAt = session.expiresAt || (session.savedAt + TOKEN_LIFETIME_MS);
    if (Date.now() >= expiresAt) {
      logger.info('Auth', 'Stored session expired, will attempt silent refresh');
      return { ...session, expiresAt, needsRefresh: true };
    }
    return { ...session, expiresAt };
  } catch (e) {
    return null;
  }
};

const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(DESKTOP_SESSION_KEY);
    currentUser = null;
    desktopAccessToken = null;
    desktopRefreshToken = null;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  } catch (e) { }
};

// Schedule automatic token refresh before expiry
const scheduleTokenRefresh = (expiresAt: number) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const timeUntilRefresh = expiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS;

  if (timeUntilRefresh <= 0) {
    logger.info('Auth', 'Token expiring soon, refreshing immediately');
    if (isTauri()) {
      desktopTokenRefresh();
    } else {
      silentRefresh();
    }
    return;
  }

  logger.debug('Auth', 'Scheduling token refresh', {
    refreshInMinutes: Math.round(timeUntilRefresh / 60000)
  });

  refreshTimer = setTimeout(() => {
    if (isTauri()) {
      desktopTokenRefresh();
    } else {
      silentRefresh();
    }
  }, timeUntilRefresh);
};

const desktopTokenRefresh = async (): Promise<boolean> => {
  if (!desktopRefreshToken) {
    logger.warn('Auth', 'No desktop refresh token available');
    return false;
  }

  try {
    logger.info('Auth', 'Starting desktop token refresh');
    const result = await desktopAuthService.refreshAccessToken(desktopRefreshToken);

    if (result) {
      desktopAccessToken = result.access_token;

      const session = localStorage.getItem(DESKTOP_SESSION_KEY);
      if (session) {
        const parsed = JSON.parse(session);
        parsed.accessToken = result.access_token;
        parsed.expiresAt = Date.now() + (result.expires_in * 1000);
        localStorage.setItem(DESKTOP_SESSION_KEY, JSON.stringify(parsed));
        scheduleTokenRefresh(parsed.expiresAt);
      }

      logger.info('Auth', 'Desktop token refresh successful');
      return true;
    }
  } catch (e) {
    logger.warn('Auth', 'Desktop token refresh failed', e);
  }
  return false;
};

// Silently refresh the token without user interaction
const silentRefresh = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!tokenClient || !currentUser) {
      logger.warn('Auth', 'Cannot refresh - no token client or user');
      resolve(false);
      return;
    }

    logger.info('Auth', 'Starting silent token refresh');

    const originalCallback = tokenClient.callback;

    tokenClient.callback = async (resp: any) => {
      tokenClient.callback = originalCallback;

      if (resp.error) {
        logger.warn('Auth', 'Silent refresh failed', { error: resp.error });
        resolve(false);
        return;
      }

      try {
        const token = gapi.client.getToken();
        const expiresAt = Date.now() + TOKEN_LIFETIME_MS;
        saveSession(currentUser!, token, expiresAt);
        logger.info('Auth', 'Silent token refresh successful');
        resolve(true);
      } catch (e) {
        logger.error('Auth', 'Error after silent refresh', e);
        resolve(false);
      }
    };

    try {
      tokenClient.requestAccessToken({ prompt: '' });
    } catch (e) {
      logger.error('Auth', 'Failed to initiate silent refresh', e);
      tokenClient.callback = originalCallback;
      resolve(false);
    }
  });
};

// Wrapper for API calls with automatic retry on auth errors
const withAuthRetry = async <T>(operation: () => Promise<T>, operationName: string): Promise<T> => {
  try {
    return await operation();
  } catch (error: any) {
    const status = error?.status || error?.result?.error?.code;
    if (status === 401 || status === 403) {
      logger.info('Auth', `${operationName} failed with ${status}, attempting token refresh`);
      const refreshed = isTauri() ? await desktopTokenRefresh() : await silentRefresh();
      if (refreshed) {
        logger.info('Auth', `Retrying ${operationName} after token refresh`);
        return await operation();
      }
    }
    throw error;
  }
};

// Wait for Google API scripts to load (handles async defer scripts)
const waitForGoogleScripts = (timeout: number = 10000): Promise<{ gapi: boolean; google: boolean }> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const pollInterval = 100; // Check every 100ms

    const checkScripts = () => {
      const hasGapi = typeof gapi !== 'undefined';
      const hasGoogle = typeof google !== 'undefined';

      if (hasGapi && hasGoogle) {
        logger.info('GoogleDrive', 'Google scripts loaded successfully');
        resolve({ gapi: true, google: true });
        return;
      }

      if (Date.now() - startTime >= timeout) {
        logger.warn('GoogleDrive', 'Timeout waiting for Google scripts', { hasGapi, hasGoogle });
        resolve({ gapi: hasGapi, google: hasGoogle });
        return;
      }

      setTimeout(checkScripts, pollInterval);
    };

    checkScripts();
  });
};

export const googleDriveService = {

  get isConfigured() {
    const hasClientId = !!config.google.clientId;
    const syncEnabled = config.features.enableDriveSync;
    const configured = hasClientId && syncEnabled;

    if (!configured) {
      logger.warn('GoogleDrive', 'Google Drive not configured', {
        hasClientId,
        clientIdLength: config.google.clientId?.length || 0,
        syncEnabled
      });
    }

    return configured;
  },

  get rootFolderId() {
    return rootFolderId;
  },

  setClientId(id: string) {
    console.warn("Please use SettingsDialog to set Client ID");
  },

  async initClients(onInitComplete: () => void) {
    logger.info('GoogleDrive', 'Initializing Google API clients', {
      isConfigured: this.isConfigured,
      hasGapi: typeof gapi !== 'undefined',
      hasGoogle: typeof google !== 'undefined',
      isTauri: isTauri()
    });

    if (isTauri()) {
      logger.info('GoogleDrive', 'Running in Tauri - using desktop OAuth');
      await desktopAuthService.initialize();
      onInitComplete();
      return;
    }

    if (!this.isConfigured) {
      logger.warn('GoogleDrive', 'Skipping init - Google Drive not configured');
      onInitComplete();
      return;
    }

    try {
      const scriptsReady = await waitForGoogleScripts(10000);

      if (!scriptsReady.gapi) {
        logger.error('GoogleDrive', 'GAPI library failed to load after waiting');
        onInitComplete();
        return;
      }

      if (!scriptsReady.google) {
        logger.error('GoogleDrive', 'Google Identity Services failed to load after waiting');
        onInitComplete();
        return;
      }

      const gapiLoadPromise = new Promise<void>((resolve, reject) => {
        gapi.load('client', async () => {
          try {
            await gapi.client.init({
              discoveryDocs: DISCOVERY_DOCS,
            });
            gapiInited = true;
            logger.info('GoogleDrive', 'GAPI client initialized successfully');
            resolve();
          } catch (e) {
            logger.error('GoogleDrive', 'Failed to init GAPI client', e);
            reject(e);
          }
        });
      });

      const gisLoadPromise = new Promise<void>((resolve, reject) => {
        try {
          if (!google.accounts?.oauth2) {
            logger.error('GoogleDrive', 'Google Identity Services oauth2 not available');
            reject(new Error('GIS oauth2 not loaded'));
            return;
          }
          tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: config.google.clientId,
            scope: SCOPES,
            callback: '',
          });
          gisInited = true;
          logger.info('GoogleDrive', 'GIS token client initialized successfully');
          resolve();
        } catch (e) {
          logger.error('GoogleDrive', 'Failed to init GIS token client', e);
          reject(e);
        }
      });

      await Promise.all([gapiLoadPromise, gisLoadPromise]);
      logger.info('GoogleDrive', 'All Google API clients initialized');
      onInitComplete();
    } catch (e) {
      logger.error('GoogleDrive', 'Failed to initialize Google clients', e);
      onInitComplete();
    }
  },

  async signIn(): Promise<User> {
    logger.info('Auth', 'Starting Google sign-in flow', {
      hasTokenClient: !!tokenClient,
      gapiInited,
      gisInited,
      isTauri: isTauri()
    });

    if (isTauri()) {
      return this.desktopSignIn();
    }

    return new Promise((resolve, reject) => {
      if (!tokenClient) {
        const error = "Google Identity Service not initialized. Check Configuration.";
        logger.error('Auth', error, {
          gapiInited,
          gisInited,
          isConfigured: this.isConfigured
        });
        return reject(error);
      }

      tokenClient.callback = async (resp: any) => {
        if (resp.error) {
          logger.error('Auth', 'OAuth callback returned error', {
            error: resp.error,
            errorDescription: resp.error_description
          });
          reject(resp);
          return;
        }

        logger.info('Auth', 'OAuth callback successful, fetching user info');

        try {
          const about = await gapi.client.drive.about.get({ fields: "user" });
          const gUser = about.result.user;

          await this.ensureRootFolder();

          const user: User = {
            id: gUser.permissionId,
            username: gUser.displayName,
            email: gUser.emailAddress,
            avatarUrl: gUser.photoLink
          };

          const token = gapi.client.getToken();
          saveSession(user, token);

          logger.info('Auth', 'Sign-in complete', { username: gUser.displayName });
          resolve(user);
        } catch (e) {
          logger.error('Auth', 'Failed to get user info after OAuth', e);
          reject(e);
        }
      };

      const hasExistingToken = gapi.client.getToken() !== null;
      logger.debug('Auth', 'Requesting access token', { hasExistingToken });

      if (!hasExistingToken) {
        tokenClient.requestAccessToken({ prompt: 'consent' });
      } else {
        tokenClient.requestAccessToken({ prompt: '' });
      }
    });
  },

  async desktopSignIn(): Promise<User> {
    logger.info('Auth', 'Starting desktop OAuth flow');

    try {
      const tokens = await desktopAuthService.signInWithGoogle();

      desktopAccessToken = tokens.access_token;
      desktopRefreshToken = tokens.refresh_token || null;

      const userInfo = await this.fetchUserInfoWithToken(tokens.access_token);

      const user: User = {
        id: userInfo.permissionId,
        username: userInfo.displayName,
        email: userInfo.emailAddress,
        avatarUrl: userInfo.photoLink
      };

      currentUser = user;

      const expiresAt = Date.now() + (tokens.expires_in * 1000);
      localStorage.setItem(DESKTOP_SESSION_KEY, JSON.stringify({
        user,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt
      }));

      scheduleTokenRefresh(expiresAt);

      logger.info('Auth', 'Desktop sign-in complete', { username: user.username });
      return user;

    } catch (error: any) {
      logger.error('Auth', 'Desktop OAuth failed', error);
      throw error;
    }
  },

  async fetchUserInfoWithToken(accessToken: string): Promise<any> {
    const response = await tauriFetch('https://www.googleapis.com/drive/v3/about?fields=user', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user info: ${response.status}`);
    }

    const data = await response.json();
    return data.user;
  },

  async logout() {
    if (isTauri()) {
      desktopAccessToken = null;
      desktopRefreshToken = null;
      localStorage.removeItem(DESKTOP_SESSION_KEY);
    } else if (typeof gapi !== 'undefined' && gapi.client?.getToken() !== null) {
      google.accounts.oauth2.revoke(gapi.client.getToken().access_token, () => { });
      gapi.client.setToken(null);
    }
    clearSession();
    rootFolderId = null;
    currentUser = null;
  },

  async tryRestoreSession(): Promise<User | null> {
    if (isTauri()) {
      return this.tryRestoreDesktopSession();
    }

    const session = loadSession() as any;
    if (!session) {
      logger.debug('Auth', 'No stored session found');
      return null;
    }

    currentUser = session.user;

    try {
      gapi.client.setToken(session.token);

      if (session.needsRefresh || (session.expiresAt && Date.now() >= session.expiresAt - TOKEN_REFRESH_BUFFER_MS)) {
        logger.info('Auth', 'Stored token expired or expiring, attempting silent refresh');
        const refreshed = await silentRefresh();
        if (!refreshed) {
          logger.warn('Auth', 'Silent refresh failed, clearing session');
          clearSession();
          gapi.client.setToken(null);
          return null;
        }
        await gapi.client.drive.about.get({ fields: "user" });
      } else {
        await gapi.client.drive.about.get({ fields: "user" });
        if (session.expiresAt) {
          scheduleTokenRefresh(session.expiresAt);
        }
      }

      await this.ensureRootFolder();

      logger.info('Auth', 'Session restored successfully', { username: session.user.username });
      return session.user;
    } catch (e: any) {
      const status = e?.status || e?.result?.error?.code;
      if (status === 401 || status === 403) {
        logger.info('Auth', 'Session restore failed with auth error, attempting silent refresh');
        const refreshed = await silentRefresh();
        if (refreshed) {
          try {
            await this.ensureRootFolder();
            logger.info('Auth', 'Session restored after silent refresh', { username: session.user.username });
            return session.user;
          } catch (retryError) {
            logger.error('Auth', 'Failed to restore session even after refresh', retryError);
          }
        }
      }

      logger.warn('Auth', 'Failed to restore session - token may be expired', e);
      clearSession();
      gapi.client.setToken(null);
      return null;
    }
  },

  async tryRestoreDesktopSession(): Promise<User | null> {
    try {
      const storedSession = localStorage.getItem(DESKTOP_SESSION_KEY);
      if (!storedSession) {
        logger.debug('Auth', 'No desktop session found');
        return null;
      }

      const session = JSON.parse(storedSession);

      if (Date.now() >= session.expiresAt - TOKEN_REFRESH_BUFFER_MS) {
        if (session.refreshToken) {
          logger.info('Auth', 'Desktop token expired, refreshing...');
          try {
            const tokens = await desktopAuthService.refreshAccessToken(session.refreshToken);
            desktopAccessToken = tokens.access_token;
            desktopRefreshToken = tokens.refresh_token || session.refreshToken;

            const expiresAt = Date.now() + (tokens.expires_in * 1000);
            localStorage.setItem(DESKTOP_SESSION_KEY, JSON.stringify({
              user: session.user,
              accessToken: tokens.access_token,
              refreshToken: desktopRefreshToken,
              expiresAt
            }));

            scheduleTokenRefresh(expiresAt);
            currentUser = session.user;
            logger.info('Auth', 'Desktop session refreshed', { username: session.user.username });
            return session.user;
          } catch (refreshError) {
            logger.warn('Auth', 'Desktop token refresh failed', refreshError);
            localStorage.removeItem(DESKTOP_SESSION_KEY);
            return null;
          }
        } else {
          logger.warn('Auth', 'Desktop token expired and no refresh token');
          localStorage.removeItem(DESKTOP_SESSION_KEY);
          return null;
        }
      }

      desktopAccessToken = session.accessToken;
      desktopRefreshToken = session.refreshToken;
      currentUser = session.user;

      const userInfo = await this.fetchUserInfoWithToken(session.accessToken);

      scheduleTokenRefresh(session.expiresAt);
      logger.info('Auth', 'Desktop session restored', { username: session.user.username });
      return session.user;

    } catch (e: any) {
      logger.warn('Auth', 'Failed to restore desktop session', e);
      localStorage.removeItem(DESKTOP_SESSION_KEY);
      return null;
    }
  },

  async ensureRootFolder(): Promise<string> {
    if (rootFolderId) {
      return rootFolderId;
    }

    if (isTauri()) {
      return this.ensureRootFolderREST();
    }

    const response = await gapi.client.drive.files.list({
      q: `name = '${ROOT_FOLDER_NAME}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
      fields: 'files(id, name)',
    });

    const files = response.result.files;
    if (files && files.length > 0) {
      rootFolderId = files[0].id;
      return rootFolderId!;
    }

    const folderMetadata = {
      name: ROOT_FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder'
    };

    const createRes = await gapi.client.drive.files.create({
      resource: folderMetadata,
      fields: 'id'
    });

    rootFolderId = createRes.result.id;
    return rootFolderId!;
  },

  async ensureRootFolderREST(): Promise<string> {
    const query = encodeURIComponent(`name = '${ROOT_FOLDER_NAME}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);
    const listRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name)`);

    if (!listRes.ok) {
      throw new Error(`Failed to list folders: ${listRes.status}`);
    }

    const listData = await listRes.json();
    if (listData.files && listData.files.length > 0) {
      rootFolderId = listData.files[0].id;
      return rootFolderId!;
    }

    const createRes = await desktopDriveRequest('https://www.googleapis.com/drive/v3/files?fields=id', {
      method: 'POST',
      body: JSON.stringify({
        name: ROOT_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });

    if (!createRes.ok) {
      throw new Error(`Failed to create root folder: ${createRes.status}`);
    }

    const createData = await createRes.json();
    rootFolderId = createData.id;
    return rootFolderId!;
  },

  async ensureProjectFolder(projectTitle: string, existingFolderId?: string): Promise<string> {
    await this.ensureRootFolder();

    if (isTauri()) {
      return this.ensureProjectFolderREST(projectTitle, existingFolderId);
    }

    if (existingFolderId) {
      try {
        await gapi.client.drive.files.get({ fileId: existingFolderId, fields: 'id' });
        return existingFolderId;
      } catch (e) {
        console.warn("[Drive] Project folder not found, creating new one");
      }
    }

    const sanitizedTitle = projectTitle.replace(/[<>:"/\\|?*]/g, '_').substring(0, 100);

    const response = await gapi.client.drive.files.list({
      q: `name = '${sanitizedTitle}' and '${rootFolderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
      fields: 'files(id, name)',
    });

    const files = response.result.files;
    if (files && files.length > 0) {
      return files[0].id;
    }

    const folderMetadata = {
      name: sanitizedTitle,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [rootFolderId]
    };

    const createRes = await gapi.client.drive.files.create({
      resource: folderMetadata,
      fields: 'id'
    });

    return createRes.result.id;
  },

  async ensureProjectFolderREST(projectTitle: string, existingFolderId?: string): Promise<string> {
    if (existingFolderId) {
      try {
        const checkRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files/${existingFolderId}?fields=id`);
        if (checkRes.ok) return existingFolderId;
      } catch { }
    }

    const sanitizedTitle = projectTitle.replace(/[<>:"/\\|?*]/g, '_').substring(0, 100);
    const query = encodeURIComponent(`name = '${sanitizedTitle}' and '${rootFolderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);

    const listRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name)`);
    if (listRes.ok) {
      const data = await listRes.json();
      if (data.files && data.files.length > 0) {
        return data.files[0].id;
      }
    }

    const createRes = await desktopDriveRequest('https://www.googleapis.com/drive/v3/files?fields=id', {
      method: 'POST',
      body: JSON.stringify({
        name: sanitizedTitle,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [rootFolderId]
      })
    });

    if (!createRes.ok) {
      throw new Error(`Failed to create project folder: ${createRes.status}`);
    }

    const data = await createRes.json();
    return data.id;
  },

  async loadDatabase(): Promise<any> {
    if (isTauri()) {
      return this.loadDatabaseREST();
    }

    return withAuthRetry(async () => {
      await this.ensureRootFolder();

      const response = await gapi.client.drive.files.list({
        q: `name = '${DB_FILENAME}' and '${rootFolderId}' in parents and trashed = false`,
        fields: 'files(id, name)',
      });

      const files = response.result.files;
      if (files && files.length > 0) {
        const fileId = files[0].id;
        const fileContent = await gapi.client.drive.files.get({
          fileId: fileId,
          alt: 'media'
        });
        return { data: fileContent.result, fileId: fileId };
      } else {
        const initialData = { ideas: [], messages: [] };
        const fileId = await this.createDatabaseFile(initialData);
        return { data: initialData, fileId };
      }
    }, 'loadDatabase');
  },

  async loadDatabaseREST(): Promise<any> {
    await this.ensureRootFolder();

    const query = encodeURIComponent(`name = '${DB_FILENAME}' and '${rootFolderId}' in parents and trashed = false`);
    const listRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files?q=${query}&fields=files(id,name)`);

    if (!listRes.ok) {
      throw new Error(`Failed to list database file: ${listRes.status}`);
    }

    const listData = await listRes.json();
    if (listData.files && listData.files.length > 0) {
      const fileId = listData.files[0].id;
      const contentRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`);

      if (!contentRes.ok) {
        throw new Error(`Failed to load database content: ${contentRes.status}`);
      }

      const data = await contentRes.json();
      return { data, fileId };
    }

    const initialData = { ideas: [], messages: [] };
    const fileId = await this.createDatabaseFile(initialData);
    return { data: initialData, fileId };
  },

  async createDatabaseFile(data: any): Promise<string> {
    if (isTauri()) {
      return this.createDatabaseFileREST(data);
    }

    return withAuthRetry(async () => {
      await this.ensureRootFolder();

      const fileContent = JSON.stringify(data);
      const metadata = {
        name: DB_FILENAME,
        mimeType: 'application/json',
        parents: [rootFolderId]
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      const accessToken = gapi.client.getToken().access_token;

      const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
        body: form
      });

      if (!res.ok && (res.status === 401 || res.status === 403)) {
        const error: any = new Error("Auth error creating database file");
        error.status = res.status;
        throw error;
      }

      const json = await res.json();
      return json.id;
    }, 'createDatabaseFile');
  },

  async createDatabaseFileREST(data: any): Promise<string> {
    await this.ensureRootFolder();

    const token = getDesktopToken();
    if (!token) throw new Error('No access token');

    const metadata = {
      name: DB_FILENAME,
      mimeType: 'application/json',
      parents: [rootFolderId]
    };

    const boundary = '-------314159265358979323846';
    const delimiter = '\r\n--' + boundary + '\r\n';
    const closeDelimiter = '\r\n--' + boundary + '--';

    const body = delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(data) +
      closeDelimiter;

    const res = await tauriFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`
      },
      body
    });

    if (!res.ok) {
      throw new Error(`Failed to create database file: ${res.status}`);
    }

    const json = await res.json();
    return json.id;
  },

  async saveDatabase(fileId: string, data: any): Promise<void> {
    if (isTauri()) {
      return this.saveDatabaseREST(fileId, data);
    }

    return withAuthRetry(async () => {
      if (!fileId) throw new Error("No file ID provided for save");

      const fileContent = JSON.stringify(data, null, 2);
      const accessToken = gapi.client.getToken().access_token;

      const res = await fetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
        method: 'PATCH',
        headers: new Headers({
          'Authorization': 'Bearer ' + accessToken,
          'Content-Type': 'application/json'
        }),
        body: fileContent
      });

      if (!res.ok) {
        const status = res.status;
        if (status === 401 || status === 403) {
          const error: any = new Error("Auth error saving to Drive");
          error.status = status;
          throw error;
        }
        throw new Error("Failed to save to Drive");
      }
    }, 'saveDatabase');
  },

  async saveDatabaseREST(fileId: string, data: any): Promise<void> {
    if (!fileId) throw new Error("No file ID provided for save");

    const token = getDesktopToken();
    if (!token) throw new Error('No access token');

    const res = await tauriFetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data, null, 2)
    });

    if (!res.ok) {
      throw new Error(`Failed to save database: ${res.status}`);
    }
  },

  async createOrUpdateGoogleDoc(
    docId: string | undefined,
    title: string,
    bodyText: string,
    folderId: string
  ): Promise<string> {
    if (isTauri()) {
      logger.warn('GoogleDrive', 'Google Docs API not available in desktop app');
      throw new Error('Google Docs creation not available in desktop app. Use web version for this feature.');
    }

    return withAuthRetry(async () => {
      let targetDocId = docId;

      if (!targetDocId) {
        const createRes = await gapi.client.docs.documents.create({ title });
        targetDocId = createRes.result.documentId;

        await gapi.client.drive.files.update({
          fileId: targetDocId,
          addParents: folderId,
          removeParents: 'root',
          fields: 'id, parents'
        });
      }

      const docRes = await gapi.client.docs.documents.get({ documentId: targetDocId });
      const content = docRes.result.body.content;
      const endIndex = content[content.length - 1].endIndex;

      const requests: any[] = [];

      if (endIndex > 2) {
        requests.push({
          deleteContentRange: {
            range: { startIndex: 1, endIndex: endIndex - 1 }
          }
        });
      }

      requests.push({
        insertText: {
          text: bodyText,
          location: { index: 1 }
        }
      });

      await gapi.client.docs.documents.batchUpdate({
        documentId: targetDocId,
        resource: { requests }
      });

      return targetDocId!;
    }, 'createOrUpdateGoogleDoc');
  },

  async uploadHtmlAsGoogleDoc(
    docId: string | undefined,
    title: string,
    htmlContent: string,
    folderId: string
  ): Promise<string> {
    return withAuthRetry(async () => {
      const boundary = '-------314159265358979323846';
      const delimiter = "\r\n--" + boundary + "\r\n";
      const closeDelimiter = "\r\n--" + boundary + "--";

      const metadata = {
        name: title,
        mimeType: 'application/vnd.google-apps.document',
        parents: docId ? undefined : [folderId] // Only set parent on create
      };

      const body = delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: text/html\r\n\r\n' +
        htmlContent +
        closeDelimiter;

      let url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
      let method = 'POST';

      // If updating, target the specific file ID with PATCH
      if (docId) {
        url = `https://www.googleapis.com/upload/drive/v3/files/${docId}?uploadType=multipart`;
        method = 'PATCH';
      }

      const accessToken = gapi.client.getToken().access_token;

      const res = await fetch(url, {
        method: method,
        headers: new Headers({
          'Authorization': 'Bearer ' + accessToken,
          'Content-Type': `multipart/related; boundary=${boundary}`
        }),
        body: body
      });

      if (!res.ok) {
        const error: any = new Error(`Failed to upload HTML Doc: ${res.status}`);
        error.status = res.status;
        throw error;
      }

      const json = await res.json();
      return json.id;
    }, 'uploadHtmlAsGoogleDoc');
  },

  async syncIdeaToDoc(idea: Idea): Promise<{ docId: string; folderId: string } | null> {
    if (isTauri()) {
      logger.debug('GoogleDrive', 'Google Docs sync skipped in desktop app');
      return null;
    }

    if (!this.isConfigured || !gapi.client.docs) {
      return null;
    }

    return withAuthRetry(async () => {
      const folderId = await this.ensureProjectFolder(idea.title, idea.googleFolderId);

      // Generate HTML Content for the Doc
      let htmlContent = `
        <html>
          <head>
            <style>
              body { font-family: 'Arial', sans-serif; font-size: 11pt; line-height: 1.5; color: #000000; background-color: #ffffff; max-width: 800px; margin: 0 auto; padding: 20px; }
              h1 { font-size: 24pt; color: #000000; margin-bottom: 10px; border-bottom: 2px solid #333; padding-bottom: 10px; }
              h2 { font-size: 18pt; color: #222222; margin-top: 25px; margin-bottom: 10px; border-bottom: 1px solid #ccc; }
              h3 { font-size: 14pt; color: #444444; margin-top: 20px; margin-bottom: 8px; }
              h4 { font-size: 12pt; color: #555555; margin-top: 15px; margin-bottom: 6px; font-weight: bold; }
              p { margin-bottom: 12px; }
              ul, ol { margin-bottom: 12px; padding-left: 30px; }
              li { margin-bottom: 5px; }
              table { border-collapse: collapse; width: 100%; margin-bottom: 15px; border: 1px solid #ccc; }
              th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
              th { background-color: #f5f5f5; font-weight: bold; }
              code { background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: 'Courier New', monospace; font-size: 0.9em; }
              blockquote { border-left: 3px solid #ccc; padding-left: 15px; margin-left: 0; color: #555; font-style: italic; }
              .meta-info { font-size: 10pt; color: #666; margin-bottom: 30px; font-style: italic; }
              .attachment-link { color: #1a73e8; text-decoration: none; }
              .attachment-box { border: 1px solid #ddd; background-color: #f9f9f9; padding: 10px; border-radius: 5px; margin-top: 10px; }
            </style>
          </head>
          <body>
            <h1>${idea.title}</h1>
            <div class="meta-info">Last Updated: ${new Date(idea.updatedAt).toLocaleString()}</div>
        `;

      const sortedSections = idea.sections?.sort((a, b) => a.order - b.order) || [];

      const processSection = (parentId: string | null, depth: number) => {
        const children = sortedSections.filter(s => s.parentId === (parentId || null));
        for (const section of children) {
          // Determine header level based on depth (h2, h3, h4...)
          // Root sections are h2 because h1 is Title
          const headerTag = depth === 0 ? 'h2' : depth === 1 ? 'h3' : 'h4';

          htmlContent += `<${headerTag}>${section.title}</${headerTag}>\n`;

          if (section.content) {
            // Content is already HTML from the editor, but we should ensure it's clean
            htmlContent += `<div class="section-content">${section.content.replace(/\n/g, '<br>')}</div>\n`;
          }

          const sectionAttachments = idea.attachments?.filter(a => a.sectionId === section.id) || [];
          if (sectionAttachments.length > 0) {
            htmlContent += `<div class="attachment-box"><strong>📎 Attachments:</strong><ul>`;
            for (const att of sectionAttachments) {
              const link = att.driveWebLink || '#';
              htmlContent += `<li><a href="${link}" class="attachment-link" target="_blank">${att.name}</a></li>`;
            }
            htmlContent += `</ul></div>`;
          }

          processSection(section.id, depth + 1);
        }
      };
      processSection(null, 0);

      const generalAttachments = idea.attachments?.filter(a => !a.sectionId) || [];
      if (generalAttachments.length > 0) {
        htmlContent += `<div class="attachment-box" style="margin-top: 30px;"><strong>📎 General Attachments:</strong><ul>`;
        for (const att of generalAttachments) {
          const link = att.driveWebLink || '#';
          htmlContent += `<li><a href="${link}" class="attachment-link" target="_blank">${att.name}</a></li>`;
        }
        htmlContent += `</ul></div>`;
      }

      htmlContent += `
          </body>
        </html>`;

      // Use a new upload method for HTML -> Google Doc conversion
      const docId = await this.uploadHtmlAsGoogleDoc(
        idea.googleDocId,
        `${idea.title}`,
        htmlContent,
        folderId
      );

      return { docId, folderId };
    }, 'syncIdeaToDoc').catch(e => {
      console.error("[Drive Sync] Failed to sync idea to Google Doc", e);
      return null;
    });
  },

  async syncChatHistoryToDoc(
    idea: Idea,
    messages: Message[]
  ): Promise<string | null> {
    if (isTauri()) {
      logger.debug('GoogleDrive', 'Chat history sync skipped in desktop app');
      return null;
    }

    if (!this.isConfigured || !gapi.client.docs) return null;
    if (!idea.googleFolderId) return null;

    return withAuthRetry(async () => {
      const projectMessages = messages.filter(m => m.ideaId === idea.id);
      if (projectMessages.length === 0) return idea.googleChatDocId || null;

      let bodyText = `CHAT HISTORY: ${idea.title.toUpperCase()}\n${'='.repeat(idea.title.length + 14)}\n\n`;
      bodyText += `Exported: ${new Date().toLocaleString()}\n`;
      bodyText += `Total Messages: ${projectMessages.length}\n\n`;
      bodyText += `${'─'.repeat(50)}\n\n`;

      for (const msg of projectMessages) {
        const timestamp = new Date(msg.timestamp).toLocaleString();
        const role = msg.role === MessageRole.USER ? 'YOU' : 'AI ASSISTANT';

        bodyText += `[${timestamp}] ${role}\n`;
        bodyText += `${'-'.repeat(30)}\n`;

        const cleanText = msg.text
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/\n{3,}/g, '\n\n')
          .trim();

        bodyText += `${cleanText}\n\n`;

        if (msg.groundingSources && msg.groundingSources.length > 0) {
          bodyText += `Sources:\n`;
          for (const source of msg.groundingSources) {
            bodyText += `  - ${source.title}: ${source.uri}\n`;
          }
          bodyText += `\n`;
        }

        bodyText += `${'─'.repeat(50)}\n\n`;
      }

      const chatDocId = await this.createOrUpdateGoogleDoc(
        idea.googleChatDocId,
        `Chat History - ${idea.title}`,
        bodyText,
        idea.googleFolderId
      );

      return chatDocId;
    }, 'syncChatHistoryToDoc').catch(e => {
      console.error("Failed to sync chat history to Google Doc", e);
      return null;
    });
  },

  async uploadExportToProject(
    idea: Idea,
    fileName: string,
    fileBlob: Blob,
    mimeType: string
  ): Promise<string | null> {
    if (!this.isConfigured || !idea.googleFolderId) return null;

    if (isTauri()) {
      return this.uploadExportToProjectREST(idea, fileName, fileBlob, mimeType);
    }

    return withAuthRetry(async () => {
      const metadata = {
        name: fileName,
        parents: [idea.googleFolderId]
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', fileBlob);

      const accessToken = gapi.client.getToken().access_token;
      if (!accessToken) throw new Error('No access token');

      const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
        body: form
      });

      if (!res.ok && (res.status === 401 || res.status === 403)) {
        const error: any = new Error("Auth error uploading to Drive");
        error.status = res.status;
        throw error;
      }

      const json = await res.json();
      return json.id;
    }, 'uploadExportToProject').catch(e => {
      console.error("Failed to upload export to Drive", e);
      return null;
    });
  },

  async uploadExportToProjectREST(
    idea: Idea,
    fileName: string,
    fileBlob: Blob,
    mimeType: string
  ): Promise<string | null> {
    return withAuthRetry(async () => {
      const token = getDesktopToken();
      if (!token) throw new DesktopDriveError('No access token', 401);

      const metadata = {
        name: fileName,
        parents: [idea.googleFolderId]
      };

      const fileData = await blobToUint8Array(fileBlob);
      const { body, contentType } = buildMultipartBody(metadata, fileData, mimeType);

      const res = await tauriFetchBinary('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': contentType
        },
        body
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new DesktopDriveError(`Auth error: ${res.status}`, res.status);
        }
        throw new Error(`Upload failed: ${res.status}`);
      }

      const json = await res.json();
      logger.info('GoogleDrive', 'Desktop export upload successful', { fileId: json.id });
      return json.id;
    }, 'uploadExportToProjectREST').catch(e => {
      logger.error('GoogleDrive', 'Failed to upload export in desktop app', e);
      return null;
    });
  },

  getProjectDrivePath(projectTitle: string): string {
    return `My Drive/${ROOT_FOLDER_NAME}/${projectTitle.replace(/[<>:"/\\|?*]/g, '_').substring(0, 100)}`;
  },

  async uploadAttachment(
    idea: Idea,
    file: File,
    sectionId?: string
  ): Promise<{ fileId: string; webLink: string } | null> {
    if (!this.isConfigured || !idea.googleFolderId) return null;

    if (isTauri()) {
      return this.uploadAttachmentREST(idea, file, sectionId);
    }

    return withAuthRetry(async () => {
      const metadata = {
        name: file.name,
        parents: [idea.googleFolderId]
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', file);

      const accessToken = gapi.client.getToken().access_token;
      if (!accessToken) throw new Error('No access token');

      const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink', {
        method: 'POST',
        headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
        body: form
      });

      if (!res.ok && (res.status === 401 || res.status === 403)) {
        const error: any = new Error("Auth error uploading attachment");
        error.status = res.status;
        throw error;
      }

      const json = await res.json();

      if (!json.id) {
        console.error("Failed to upload attachment:", json);
        return null;
      }

      await gapi.client.drive.permissions.create({
        fileId: json.id,
        resource: {
          role: 'reader',
          type: 'anyone'
        }
      });

      const fileRes = await gapi.client.drive.files.get({
        fileId: json.id,
        fields: 'webViewLink'
      });

      return {
        fileId: json.id,
        webLink: fileRes.result.webViewLink || `https://drive.google.com/file/d/${json.id}/view`
      };
    }, 'uploadAttachment').catch(e => {
      console.error("Failed to upload attachment to Drive", e);
      return null;
    });
  },

  async uploadAttachmentREST(
    idea: Idea,
    file: File,
    sectionId?: string
  ): Promise<{ fileId: string; webLink: string } | null> {
    return withAuthRetry(async () => {
      const token = getDesktopToken();
      if (!token) throw new DesktopDriveError('No access token', 401);

      const metadata = {
        name: file.name,
        parents: [idea.googleFolderId]
      };

      const fileData = await fileToUint8Array(file);
      const { body, contentType } = buildMultipartBody(metadata, fileData, file.type || 'application/octet-stream');

      const res = await tauriFetchBinary('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': contentType
        },
        body
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new DesktopDriveError(`Auth error: ${res.status}`, res.status);
        }
        throw new Error(`Upload failed: ${res.status}`);
      }

      const json = await res.json();

      if (!json.id) {
        logger.error('GoogleDrive', 'Failed to upload attachment - no file ID returned', json);
        return null;
      }

      await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files/${json.id}/permissions`, {
        method: 'POST',
        body: JSON.stringify({ role: 'reader', type: 'anyone' })
      });

      const fileRes = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files/${json.id}?fields=webViewLink`);
      const fileInfo = await fileRes.json();

      logger.info('GoogleDrive', 'Desktop attachment upload successful', { fileId: json.id });
      return {
        fileId: json.id,
        webLink: fileInfo.webViewLink || `https://drive.google.com/file/d/${json.id}/view`
      };
    }, 'uploadAttachmentREST').catch(e => {
      logger.error('GoogleDrive', 'Failed to upload attachment in desktop app', e);
      return null;
    });
  },

  async deleteAttachment(fileId: string): Promise<boolean> {
    if (!this.isConfigured) return false;

    return withAuthRetry(async () => {
      if (isTauri()) {
        const res = await desktopDriveRequest(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
          method: 'DELETE'
        });
        return res.ok;
      }

      await gapi.client.drive.files.delete({ fileId });
      return true;
    }, 'deleteAttachment').catch(e => {
      console.error("Failed to delete attachment from Drive", e);
      return false;
    });
  }
};